import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { ButtonModule } from 'primeng/button';

/**
 * Inline-notification severity.
 *
 * `success` is intentionally excluded: per the styleguide, success feedback is
 * strictly transient (toast only) and is never presented inline.
 */
export type InlineNotificationSeverity = 'info' | 'warn' | 'error';

/**
 * Inline-notification layout.
 *
 * - `block` — the notification REPLACES missing content (empty result area,
 *   failed load, tile without a figure): centered, stacked, no surface.
 * - `compact` — the notification sits ALONGSIDE content (dialogs, forms, the
 *   warning strip at the foot of a tile): the p-message shape — a left-aligned
 *   row on a tinted surface with a hairline border.
 */
export type InlineNotificationVariant = 'block' | 'compact';

interface SeverityStyle {
  /** Fixed PrimeIcons glyph for the severity. */
  readonly icon: string;
  /** ARIA live-region role — passive `status` for info, assertive `alert` for warn/error. */
  readonly role: 'status' | 'alert';
}

/** Fixed severity → icon + a11y-role mapping (layout/spacing/type stay constant). */
const SEVERITY_STYLES: Record<InlineNotificationSeverity, SeverityStyle> = {
  info: { icon: 'pi pi-info-circle', role: 'status' },
  warn: { icon: 'pi pi-exclamation-triangle', role: 'alert' },
  error: { icon: 'pi pi-times-circle', role: 'alert' },
};

/**
 * Persistent inline notification (styleguide "Notifications & messages" → inline type).
 *
 * Integrates into the content where a result is expected: no background, no border,
 * centered, fills the parent width and persists until the cause changes. Parts are
 * stacked and centered in fixed order — icon (in a soft tinted circle) · title ·
 * explanation · optional action. Severity decides only the icon + accent colour;
 * layout, spacing and type stay constant. There is no close button (toast-only
 * affordance) and at most one action.
 *
 * ## Two variants
 *
 * `variant="block"` (default) is the full-area state described above. Inside a
 * dialog or next to a form it is far too tall, so `variant="compact"` renders
 * the same parts in the p-message shape: a 20px icon on the left, title,
 * explanation and action stacked to its right, on the severity tint with a
 * hairline border. Severity, icon, role and the one-action rule are identical.
 *
 * ## The action may be any control, not just a text button
 *
 * The styleguide constrains the action's COUNT and ROLE — "At most one action
 * per message, leading directly to the resolution" — and says nothing about
 * which control carries it. So besides `actionLabel` (the convenient text
 * button for the common case) an arbitrary control can be projected into the
 * same slot, keeping the fixed order and the centered layout:
 *
 * ```html
 * <bd-ui-inline-notification title="Kein Jahresabschluss für 2024" …>
 *   <p-selectbutton notificationAction … />
 * </bd-ui-inline-notification>
 * ```
 *
 * `actionLabel` wins when both are given, so the slot cannot quietly become a
 * second action next to the button — the one-action rule stays enforceable by
 * reading the template.
 */
@Component({
  selector: 'bd-ui-inline-notification',
  imports: [ButtonModule],
  template: `
    <div [class]="rootClass()" [attr.role]="role()">
      <span class="s-ico"
        ><i [class]="iconClass()" aria-hidden="true"></i
      ></span>
      <span class="s-title">{{ title() }}</span>
      @if (message()) {
        <p class="s-message">{{ message() }}</p>
      }
      @if (actionLabel()) {
        <button
          pButton
          text
          size="small"
          class="s-action"
          (click)="action.emit()"
        >
          <i [class]="actionIcon()" aria-hidden="true"></i
          ><span>{{ actionLabel() }}</span>
        </button>
      } @else {
        <div class="s-action s-slot"><ng-content select="[notificationAction]" /></div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .inline-notification {
        width: 100%;
        border: 0;
        background: transparent;
        text-align: center;
        display: grid;
        place-items: center;
        gap: 0.625rem;
        padding: 3rem 1.5rem;
      }
      .s-ico {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        display: grid;
        place-items: center;
        color: var(--_accent);
        background: var(--_tint);
      }
      .s-ico i {
        font-size: 22px;
        line-height: 1;
      }
      .s-title {
        font-size: 1rem;
        font-weight: 600;
        color: light-dark(#1f2430, var(--p-text-color));
      }
      .s-message {
        margin: 0;
        max-width: 380px;
        font-size: 0.875rem;
        color: light-dark(#5a6170, var(--p-text-muted-color));
      }
      .s-action {
        margin-top: 0.375rem;
      }
      /*
        The action follows the message severity instead of the primary orange,
        so one message carries one accent. Text uses the readable on-tint ink —
        for warn the shared warning ink, never the bright orange.
      */
      button.s-action {
        color: var(--_ink);
      }
      button.s-action:not(:disabled):hover,
      button.s-action:not(:disabled):active {
        color: var(--_ink);
        background: color-mix(in srgb, var(--_ink) 12%, transparent);
      }
      /*
        The projected action slot. The :empty rule hides it when nothing was
        projected, so the grid's row gap does not open a hole under the
        explanation. The template therefore writes the ng-content with NO
        whitespace inside the div: a whitespace text node would defeat :empty,
        while the comment node Angular leaves behind does not.
      */
      .s-slot:empty {
        display: none;
      }
      /*
        Compact variant — same DOM, different layout. The icon takes column 1
        across every row; title, explanation and action all sit in column 2,
        left-aligned. The empty-slot :empty rule above still removes the
        action row when nothing is projected.
      */
      .inline-notification.compact {
        grid-template-columns: 20px 1fr;
        place-items: start;
        column-gap: 0.75rem;
        row-gap: 0.125rem;
        padding: 0.75rem 0.875rem;
        text-align: left;
        border: 1px solid var(--_border);
        border-radius: 8px;
        background: var(--_tint);
      }
      .compact > * {
        grid-column: 2;
      }
      .compact .s-ico {
        grid-column: 1;
        grid-row: 1 / span 3;
        width: 20px;
        height: 20px;
        background: transparent;
      }
      .compact .s-ico i {
        font-size: 18px;
      }
      .compact .s-title {
        font-size: 0.875rem;
        line-height: 20px;
      }
      .compact .s-message {
        max-width: none;
      }
      .compact .s-action {
        margin-top: 0.25rem;
      }
      .compact button.s-action {
        margin-inline-start: -0.75rem;
      }
      /*
        Ink and tint are the shared status tokens (theme-lib bd-colors.ts,
        BDA-6801): AA on the tint in both schemes. Warn keeps the brand
        orange as its non-text accent (icon, border).
      */
      .sev-info {
        --_ink: var(--p-bd-status-info-color);
        --_accent: var(--_ink);
        --_tint: var(--p-bd-status-info-tint);
        --_border: color-mix(in srgb, var(--_ink) 20%, transparent);
      }
      .sev-warn {
        --_ink: var(--p-bd-status-warning-color);
        --_accent: #f58b25;
        --_tint: var(--p-bd-status-warning-tint);
        --_border: color-mix(in srgb, #f58b25 28%, transparent);
      }
      .sev-error {
        --_ink: var(--p-bd-status-error-color);
        --_accent: var(--_ink);
        --_tint: var(--p-bd-status-error-tint);
        --_border: color-mix(in srgb, var(--_ink) 24%, transparent);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BdUiInlineNotificationComponent {
  /** Severity — decides icon + accent colour only. Defaults to `info`. */
  readonly severity = input<InlineNotificationSeverity>('info');
  /**
   * Layout — `block` (default) replaces missing content, `compact` sits next to
   * content in dialogs and forms. See {@link InlineNotificationVariant}.
   */
  readonly variant = input<InlineNotificationVariant>('block');
  /** Short title, no trailing punctuation. */
  readonly title = input.required<string>();
  /** Optional plain-language explanation (rendered muted). No stack traces / error codes. */
  readonly message = input<string>('');
  /**
   * Overrides the severity's fixed glyph. Use only where the styleguide asks
   * for a different symbol — the documented case is an empty **search** result,
   * which stays neutral (`info`) but is marked with a search icon rather than
   * the info circle. Severity still owns the accent colour and the ARIA role,
   * so an override cannot turn a notice into a different kind of message.
   *
   * Leave unset everywhere else: one fixed symbol per severity is what makes
   * the four states recognisable at a glance.
   */
  readonly icon = input<string>('');
  /** Optional single action label. When set, a text button is rendered. */
  readonly actionLabel = input<string>('');
  /** Optional leading PrimeIcons glyph for the action (e.g. `pi pi-refresh`). */
  readonly actionIcon = input<string>('');

  /** Emitted when the optional action button is clicked. */
  readonly action = output<void>();

  /** Root class string — base layout class, compact modifier and severity accent. */
  readonly rootClass = computed(() =>
    [
      'inline-notification',
      this.variant() === 'compact' ? 'compact' : '',
      `sev-${this.severity()}`,
    ]
      .filter(Boolean)
      .join(' '),
  );
  /** Class string for the leading icon — the severity glyph unless overridden. */
  readonly iconClass = computed(
    () => this.icon() || SEVERITY_STYLES[this.severity()].icon,
  );
  /** ARIA live-region role derived from severity. */
  readonly role = computed(() => SEVERITY_STYLES[this.severity()].role);
}
